/**
 * Public reservation flow (Fase C + D).
 *
 * Multi-step:
 *   1. Tipo (solo adultos / con niños / adulto+bebé) + personas
 *   2. Fecha y hora
 *   3. Ficha de cliente (con búsqueda por DNI/email para precargar)
 *   4. Cuestionario de salud (bloquea si marca algo)
 *   5. Consentimientos (3 checks)
 *   6. Pago 50% Redsys
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Check, ArrowLeft, Loader2, Users, Calendar as CalIcon, Heart, FileText as FileIcon, CreditCard, Tag } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { label: 'Tipo', icon: Users },
  { label: 'Fecha', icon: CalIcon },
  { label: 'Tus datos', icon: FileIcon },
  { label: 'Salud', icon: Heart },
  { label: 'Consentimientos', icon: Check },
  { label: 'Pago', icon: CreditCard },
];

const HEALTH_ITEMS = [
  { key: 'alergias_respiratorias', label: 'Alergias respiratorias' },
  { key: 'congestion_nasal', label: 'Congestión nasal frecuente' },
  { key: 'sensibilidad_respiratoria', label: 'Sensibilidad respiratoria' },
  { key: 'asma', label: 'Asma' },
  { key: 'bronquitis_recurrente', label: 'Bronquitis recurrente' },
  { key: 'problemas_piel', label: 'Problemas de piel / piel sensible' },
  { key: 'estres', label: 'Estrés / dificultad para relajarse' },
];

const initialState = {
  // Step 1
  booking_type: 'solo_adultos',
  num_adults: 1,
  num_children: 0,
  num_babies: 0,
  // Step 2
  date: '',
  time: '',
  duration_minutes: 60,
  // Step 3
  customer: {
    full_name: '',
    birth_date: '',
    dni: '',
    phone: '',
    email: '',
    address: '',
    is_minor: false,
    guardian_name: '',
    guardian_dni: '',
    guardian_phone: '',
    notes: '',
  },
  customer_number_found: null,
  // Step 4
  health: HEALTH_ITEMS.reduce((acc, it) => ({ ...acc, [it.key]: false }), {
    otros: false,
    otros_texto: '',
    fiebre_contagiosa: false,
    tratamiento_medico: false,
  }),
  // Step 5
  consents: {
    accepted_data_consent: false,
    accepted_disclaimer: false,
    accepted_cancellation_policy: false,
  },
  // Optional promo code (step 6 — payment)
  promo_code: '',
  promo_applied: null, // { code, discount_type, discount_percent, discount_amount, description }
};

export const ReservarPage = () => {
  const [step, setStep] = useState(0);
  const [state, setState] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const nextStep = () => {
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const prevStep = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Determine room from type
  const room = state.booking_type === 'solo_adultos' ? 'sala_grande' : 'sala_pequena';

  const checkHealthBlocked = () => {
    const h = state.health;
    return Object.entries(h).some(
      ([k, v]) => v === true && k !== 'otros_texto'
    );
  };

  const allConsentsAccepted = Object.values(state.consents).every((v) => v === true);

  const submitReservation = async () => {
    if (checkHealthBlocked()) {
      toast.error('Por tu seguridad no podemos procesar esta reserva online. Contacta con nosotros.');
      return;
    }
    if (!allConsentsAccepted) {
      toast.error('Debes aceptar los tres consentimientos.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        booking_type: state.booking_type,
        num_adults: state.num_adults,
        num_children: state.num_children,
        num_babies: state.num_babies,
        date: state.date,
        time: state.time,
        duration_minutes: state.duration_minutes,
        customer: {
          ...state.customer,
          // strip empty strings
          ...(state.customer.birth_date ? {} : { birth_date: undefined }),
          ...(state.customer.dni ? {} : { dni: undefined }),
          ...(state.customer.email ? {} : { email: undefined }),
        },
        health: state.health,
        consents: state.consents,
        ...(state.promo_applied?.code ? { promo_code: state.promo_applied.code } : {}),
      };
      // remove undefined explicitly
      Object.keys(payload.customer).forEach((k) => {
        if (payload.customer[k] === undefined || payload.customer[k] === '') {
          delete payload.customer[k];
        }
      });
      const res = await axios.post(`${API}/bookings/public`, payload);
      const { redsys_url, payment_data } = res.data;
      const formEl = document.createElement('form');
      formEl.method = 'POST';
      formEl.action = redsys_url;
      Object.entries(payment_data).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = v;
        formEl.appendChild(input);
      });
      document.body.appendChild(formEl);
      formEl.submit();
    } catch (err) {
      setSubmitting(false);
      toast.error(err.response?.data?.detail || 'Error al crear la reserva');
    }
  };

  return (
    <div className="min-h-screen bg-halo-cream" data-testid="reservar-page">
      {/* Hero */}
      <section className="py-12 bg-halo-charcoal">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="font-heading text-3xl md:text-4xl text-halo-cream mb-2">Reservar sesión</h1>
          <p className="text-halo-cream/70 text-sm">Sesión individual con pago del 50% como depósito</p>
        </div>
      </section>

      {/* Progress */}
      <section className="py-6 bg-halo-surface border-b border-halo-taupe/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between overflow-x-auto">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="flex items-center flex-shrink-0">
                  <div
                    className={`flex items-center justify-center w-9 h-9 rounded-full text-xs transition-all ${
                      i < step
                        ? 'bg-halo-sage text-halo-cream'
                        : i === step
                        ? 'bg-halo-brown text-halo-cream'
                        : 'bg-halo-cream text-halo-text border border-halo-taupe/20'
                    }`}
                  >
                    {i < step ? <Check size={14} /> : <Icon size={14} />}
                  </div>
                  <span
                    className={`ml-2 text-xs hidden md:block ${
                      i <= step ? 'text-halo-charcoal font-medium' : 'text-halo-text'
                    }`}
                  >
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`w-4 lg:w-8 h-0.5 mx-2 ${
                        i < step ? 'bg-halo-sage' : 'bg-halo-taupe/20'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-3xl mx-auto px-6">
          {step > 0 && (
            <button
              onClick={prevStep}
              className="mb-4 inline-flex items-center gap-1 text-sm text-halo-text hover:text-halo-charcoal"
              data-testid="reservar-back"
            >
              <ArrowLeft size={16} /> Atrás
            </button>
          )}

          {step === 0 && <StepType state={state} setState={setState} onNext={nextStep} room={room} />}
          {step === 1 && <StepDate state={state} setState={setState} onNext={nextStep} />}
          {step === 2 && <StepCustomer state={state} setState={setState} onNext={nextStep} />}
          {step === 3 && (
            <StepHealth
              state={state}
              setState={setState}
              onNext={nextStep}
              isBlocked={checkHealthBlocked()}
            />
          )}
          {step === 4 && (
            <StepConsents
              state={state}
              setState={setState}
              onNext={nextStep}
              allAccepted={allConsentsAccepted}
            />
          )}
          {step === 5 && (
            <StepPayment
              state={state}
              setState={setState}
              submitting={submitting}
              onPay={submitReservation}
            />
          )}
        </div>
      </section>
    </div>
  );
};

const Card = ({ title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-halo-surface rounded-2xl p-6 md:p-8 border border-halo-taupe/10"
  >
    <h2 className="font-heading text-2xl text-halo-charcoal mb-6">{title}</h2>
    {children}
  </motion.div>
);

const StepType = ({ state, setState, onNext, room }) => {
  const types = [
    { value: 'solo_adultos', label: 'Solo adultos', desc: 'Sala 2 (grande, hasta 9 adultos)' },
    { value: 'con_ninos', label: 'Con niños', desc: 'Sala 1 (pequeña). Menores de 14 años' },
    { value: 'adulto_bebe', label: 'Adulto con bebé', desc: 'Sala 1 (pequeña). Bebé menor de 12 meses' },
  ];

  const canProceed = () => {
    if (state.num_adults < 1) return false;
    if (state.booking_type === 'con_ninos' && state.num_children < 1) return false;
    if (state.booking_type === 'adulto_bebe' && state.num_babies < 1) return false;
    return true;
  };

  return (
    <Card title="¿Cómo será tu reserva?">
      <div className="grid gap-3 mb-6">
        {types.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() =>
              setState((p) => ({
                ...p,
                booking_type: t.value,
                num_children: t.value !== 'con_ninos' ? 0 : p.num_children,
                num_babies: t.value !== 'adulto_bebe' ? 0 : Math.max(1, p.num_babies),
              }))
            }
            className={`text-left p-4 rounded-xl border-2 transition-all ${
              state.booking_type === t.value
                ? 'border-halo-brown bg-halo-brown/5'
                : 'border-halo-taupe/20 hover:border-halo-brown/40'
            }`}
            data-testid={`type-${t.value}`}
          >
            <p className="font-medium text-halo-charcoal">{t.label}</p>
            <p className="text-xs text-halo-text mt-1">{t.desc}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <Label>Adultos</Label>
          <Input
            type="number"
            min="1"
            value={state.num_adults}
            onChange={(e) =>
              setState((p) => ({ ...p, num_adults: parseInt(e.target.value) || 1 }))
            }
            className="mt-1"
            data-testid="adults-input"
          />
        </div>
        <div>
          <Label className="text-xs">Niños (≤14)</Label>
          <Input
            type="number"
            min="0"
            disabled={state.booking_type !== 'con_ninos'}
            value={state.num_children}
            onChange={(e) =>
              setState((p) => ({ ...p, num_children: parseInt(e.target.value) || 0 }))
            }
            className="mt-1"
            data-testid="children-input"
          />
        </div>
        <div>
          <Label className="text-xs">Bebés (&lt;12m)</Label>
          <Input
            type="number"
            min="0"
            disabled={state.booking_type !== 'adulto_bebe'}
            value={state.num_babies}
            onChange={(e) =>
              setState((p) => ({ ...p, num_babies: parseInt(e.target.value) || 0 }))
            }
            className="mt-1"
            data-testid="babies-input"
          />
        </div>
      </div>

      <p className="text-xs text-halo-text mb-6">
        Sala asignada: <strong>{room === 'sala_grande' ? 'Sala 2 (grande)' : 'Sala 1 (pequeña)'}</strong>
      </p>

      {state.booking_type === 'con_ninos' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-xs text-amber-900">
          ⚠️ Los menores de 14 años deben ir acompañados siempre por un adulto. Tanto adultos como niños deben adquirir su entrada.
        </div>
      )}

      <Button
        onClick={onNext}
        disabled={!canProceed()}
        className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
        data-testid="step-type-next"
      >
        Continuar
      </Button>
    </Card>
  );
};

const StepDate = ({ state, setState, onNext }) => {
  const slots = [];
  for (let h = 9; h <= 21; h++) {
    for (const m of [0, 15, 30, 45]) {
      if (h === 21 && m > 0) continue;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  return (
    <Card title="¿Cuándo te gustaría venir?">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <Label>Fecha *</Label>
          <Input
            type="date"
            value={state.date}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setState((p) => ({ ...p, date: e.target.value }))}
            className="mt-1"
            data-testid="date-input"
            required
          />
        </div>
        <div>
          <Label>Hora *</Label>
          <select
            value={state.time}
            onChange={(e) => setState((p) => ({ ...p, time: e.target.value }))}
            className="mt-1 w-full rounded-md border border-halo-taupe/20 px-3 py-2 bg-white"
            data-testid="time-input"
          >
            <option value="">Selecciona hora</option>
            {slots.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-xs text-halo-text mb-6">
        Sesión de aproximadamente 60 minutos. Te llamaremos para confirmar la disponibilidad.
      </p>

      <Button
        onClick={onNext}
        disabled={!state.date || !state.time}
        className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
        data-testid="step-date-next"
      >
        Continuar
      </Button>
    </Card>
  );
};

const StepCustomer = ({ state, setState, onNext }) => {
  const [searching, setSearching] = useState(false);

  const set = (k, v) => setState((p) => ({ ...p, customer: { ...p.customer, [k]: v } }));

  // Auto-lookup by DNI/email
  useEffect(() => {
    const t = setTimeout(async () => {
      const dni = state.customer.dni?.trim();
      const email = state.customer.email?.trim();
      if (!dni && !email) return;
      setSearching(true);
      try {
        const params = new URLSearchParams();
        if (dni) params.set('dni', dni);
        if (email) params.set('email', email);
        const res = await axios.get(`${API}/customers/lookup?${params}`);
        if (res.data.found) {
          toast.success(`Te encontramos, ${res.data.full_name} (${res.data.customer_number})`);
          setState((p) => ({
            ...p,
            customer_number_found: res.data.customer_number,
            customer: {
              ...p.customer,
              full_name: p.customer.full_name || res.data.full_name,
              phone: p.customer.phone || res.data.phone,
              email: p.customer.email || res.data.email || '',
            },
          }));
        }
      } catch (err) {
        // Customer lookup is best-effort — most failures here mean "not found",
        // which is the expected state for a new client. Log for dev visibility
        // but do not interrupt the user.
        if (err?.response?.status && err.response.status >= 500) {
          console.error('Customer lookup failed:', err);
        }
      } finally {
        setSearching(false);
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.customer.dni, state.customer.email]);

  const ready =
    state.customer.full_name.trim() &&
    state.customer.phone.trim() &&
    (state.customer.email || '').includes('@');

  return (
    <Card title="Tus datos">
      {state.customer_number_found && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-800">
          ✓ Ya eras cliente. Nº: <strong>{state.customer_number_found}</strong>
        </div>
      )}

      <div className="grid gap-4">
        <div>
          <Label>Nombre y apellidos *</Label>
          <Input
            value={state.customer.full_name}
            onChange={(e) => set('full_name', e.target.value)}
            className="mt-1"
            data-testid="cust-name"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Fecha de nacimiento</Label>
            <Input
              type="date"
              value={state.customer.birth_date}
              onChange={(e) => set('birth_date', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>DNI / NIE</Label>
            <Input
              value={state.customer.dni}
              onChange={(e) => set('dni', e.target.value)}
              className="mt-1"
              placeholder="12345678A"
              data-testid="cust-dni"
            />
            {searching && <p className="text-xs text-halo-text mt-1">Buscando...</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Teléfono *</Label>
            <Input
              type="tel"
              value={state.customer.phone}
              onChange={(e) => set('phone', e.target.value)}
              className="mt-1"
              data-testid="cust-phone"
              required
            />
          </div>
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={state.customer.email}
              onChange={(e) => set('email', e.target.value)}
              className="mt-1"
              data-testid="cust-email"
              required
            />
          </div>
        </div>
        <div>
          <Label>Dirección</Label>
          <Input
            value={state.customer.address}
            onChange={(e) => set('address', e.target.value)}
            className="mt-1"
            placeholder="Calle, número, ciudad"
          />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={state.customer.is_minor}
            onChange={(e) => set('is_minor', e.target.checked)}
          />
          Asistiré con un menor de edad (rellenar datos del tutor)
        </label>

        {state.customer.is_minor && (
          <div className="bg-amber-50 rounded-lg p-4 space-y-3">
            <Input
              placeholder="Nombre del padre/madre/tutor legal"
              value={state.customer.guardian_name}
              onChange={(e) => set('guardian_name', e.target.value)}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="DNI/NIE del tutor"
                value={state.customer.guardian_dni}
                onChange={(e) => set('guardian_dni', e.target.value)}
              />
              <Input
                placeholder="Tlf. del tutor"
                value={state.customer.guardian_phone}
                onChange={(e) => set('guardian_phone', e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      <Button
        onClick={onNext}
        disabled={!ready}
        className="w-full mt-6 bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
        data-testid="step-customer-next"
      >
        Continuar
      </Button>
    </Card>
  );
};

const StepHealth = ({ state, setState, onNext, isBlocked }) => {
  const set = (k, v) => setState((p) => ({ ...p, health: { ...p.health, [k]: v } }));

  return (
    <Card title="Cuestionario de salud">
      <p className="text-sm text-halo-text mb-4">
        Marca <strong>únicamente si procede</strong>. Esta información es importante para tu seguridad.
      </p>

      <div className="grid gap-2 mb-6">
        {HEALTH_ITEMS.map((it) => (
          <label
            key={it.key}
            className="flex items-center gap-2 p-3 rounded-lg border border-halo-taupe/15 cursor-pointer hover:bg-halo-cream/40"
          >
            <input
              type="checkbox"
              checked={state.health[it.key]}
              onChange={(e) => set(it.key, e.target.checked)}
              data-testid={`health-${it.key}`}
            />
            <span className="text-sm text-halo-charcoal">{it.label}</span>
          </label>
        ))}
        <label className="flex items-center gap-2 p-3 rounded-lg border border-halo-taupe/15 cursor-pointer hover:bg-halo-cream/40">
          <input
            type="checkbox"
            checked={state.health.otros}
            onChange={(e) => set('otros', e.target.checked)}
            data-testid="health-otros"
          />
          <span className="text-sm text-halo-charcoal">Otros:</span>
          <Input
            disabled={!state.health.otros}
            value={state.health.otros_texto}
            onChange={(e) => set('otros_texto', e.target.value)}
            className="ml-2 h-8 text-sm"
            placeholder="Describir..."
          />
        </label>
      </div>

      <div className="space-y-3 mb-6 pt-4 border-t border-halo-taupe/10">
        <YesNo
          label="¿Actualmente presenta fiebre o enfermedad contagiosa?"
          value={state.health.fiebre_contagiosa}
          onChange={(v) => set('fiebre_contagiosa', v)}
          testid="fiebre"
        />
        <YesNo
          label="¿Está realizando algún tratamiento médico en el último año? (Quimio, radioterapia, diálisis, hipertensión...)"
          value={state.health.tratamiento_medico}
          onChange={(v) => set('tratamiento_medico', v)}
          testid="tratamiento"
        />
      </div>

      {isBlocked ? (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 text-sm text-red-900">
          <p className="font-medium mb-2">No podemos confirmar tu reserva online</p>
          <p className="leading-relaxed mb-3">
            Para tu seguridad y poder personalizar tu visita, necesitamos hablar contigo
            antes de confirmar esta reserva. Por favor escríbenos a{' '}
            <a href="mailto:info@halocuevas.com" className="underline font-bold">
              info@halocuevas.com
            </a>{' '}
            indicándonos tu situación. Te responderemos rápidamente y, si procede, te
            enviaremos los consentimientos a firmar antes de tu primera sesión.
          </p>
          <p className="text-xs">
            También puedes llamarnos al 678 901 086.
          </p>
        </div>
      ) : (
        <Button
          onClick={onNext}
          className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
          data-testid="step-health-next"
        >
          Continuar
        </Button>
      )}
    </Card>
  );
};

const YesNo = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-halo-charcoal flex-1">{label}</span>
    <div className="flex gap-2 flex-shrink-0">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-3 py-1 rounded-full text-xs ${
          value === true
            ? 'bg-red-500 text-white'
            : 'bg-halo-cream text-halo-charcoal border border-halo-taupe/20'
        }`}
        data-testid={`${testid}-yes`}
      >
        Sí
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-3 py-1 rounded-full text-xs ${
          value === false
            ? 'bg-halo-sage text-halo-cream'
            : 'bg-halo-cream text-halo-charcoal border border-halo-taupe/20'
        }`}
        data-testid={`${testid}-no`}
      >
        No
      </button>
    </div>
  </div>
);

const StepConsents = ({ state, setState, onNext, allAccepted }) => {
  const set = (k, v) => setState((p) => ({ ...p, consents: { ...p.consents, [k]: v } }));

  return (
    <Card title="Consentimientos">
      <ConsentBlock
        title="Información sobre la Haloterapia"
        accepted={state.consents.accepted_disclaimer}
        onChange={(v) => set('accepted_disclaimer', v)}
        testid="consent-disclaimer"
        textPreview="La haloterapia es una actividad de bienestar complementaria que reproduce el microclima de las minas de sal..."
      >
        <p className="mb-3">
          La haloterapia o terapia de sal es una actividad de bienestar complementaria que
          reproduce de forma controlada el microclima natural de las minas de sal mediante la
          dispersión de micropartículas de sal en el ambiente.
        </p>
        <p className="mb-3">
          Las sesiones se realizan en cuevas de sal acondicionadas para favorecer un entorno
          de relajación, bienestar y confort respiratorio, tanto para adultos como para niños.
          Muchas personas recurren a la haloterapia como complemento en situaciones relacionadas
          con congestión nasal, alergias, sensibilidad respiratoria, asma, bronquitis recurrentes,
          estrés o determinadas afecciones cutáneas como piel sensible o atópica.
        </p>
        <p className="mb-3">
          La haloterapia <strong>no sustituye en ningún caso tratamientos médicos</strong>,
          diagnósticos ni seguimientos realizados por profesionales sanitarios. Se trata de una
          experiencia complementaria orientada al bienestar y relajación. Ante cualquier duda
          o patología se recomienda seguir siempre las indicaciones de su médico.
        </p>
        <p>
          Al realizar la sesión, el usuario declara haber sido informado sobre la actividad de
          haloterapia y comprender que sus beneficios pueden variar según cada persona.
        </p>
      </ConsentBlock>

      <ConsentBlock
        title="Política de reserva y cancelación"
        accepted={state.consents.accepted_cancellation_policy}
        onChange={(v) => set('accepted_cancellation_policy', v)}
        testid="consent-cancellation"
        textPreview="Pago anticipado del 50% del importe. Modificaciones/cancelaciones sin coste hasta 24h antes..."
      >
        <p className="mb-3">
          Para formalizar la reserva será necesario realizar un pago anticipado correspondiente
          al <strong>50% del importe</strong> del servicio reservado mediante la pasarela de pago
          habilitada en la web.
        </p>
        <p className="mb-3">
          Las reservas podrán modificarse o cancelarse <strong>sin coste</strong> siempre que se
          comuniquen con un mínimo de <strong>24 horas de antelación</strong> respecto a la hora
          de la sesión. En estos casos, el importe abonado podrá utilizarse para reagendar.
        </p>
        <p className="mb-3">
          En caso de cancelación con menos de 24 horas de antelación o de no asistencia, el cliente
          acepta la <strong>pérdida del importe abonado</strong>, correspondiente al 50% del servicio,
          debido al bloqueo de la sala y la imposibilidad de reasignar la franja con tan poca antelación.
        </p>
        <p>
          Si por motivos organizativos el centro no pudiera realizar la sesión, se ofrecerá una
          nueva fecha o la <strong>devolución íntegra</strong> del importe abonado.
        </p>
      </ConsentBlock>

      <ConsentBlock
        title="Consentimiento de tratamiento de datos (RGPD)"
        accepted={state.consents.accepted_data_consent}
        onChange={(v) => set('accepted_data_consent', v)}
        testid="consent-data"
        textPreview="Acepto el tratamiento de mis datos según la política de privacidad..."
      >
        <p className="mb-3">
          <em>[Texto pendiente de publicación definitiva]</em>
        </p>
        <p className="mb-3">
          Acepto que HALO Cuevas de Sal trate mis datos personales con la finalidad de gestionar
          mi reserva, contacto, facturación y cumplimiento de obligaciones legales, conforme al
          Reglamento (UE) 2016/679 (RGPD).
        </p>
        <p>
          Podré ejercer mis derechos de acceso, rectificación, supresión, oposición, limitación y
          portabilidad escribiendo a info@halocuevas.com.
        </p>
      </ConsentBlock>

      <Button
        onClick={onNext}
        disabled={!allAccepted}
        className="w-full mt-2 bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
        data-testid="step-consents-next"
      >
        Continuar al pago
      </Button>
    </Card>
  );
};

const ConsentBlock = ({ title, accepted, onChange, testid, children, textPreview }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-halo-taupe/15 rounded-xl p-4 mb-4">
      <h3 className="font-medium text-halo-charcoal mb-2">{title}</h3>
      {!expanded ? (
        <p className="text-xs text-halo-text mb-2 italic line-clamp-2">{textPreview}</p>
      ) : (
        <div className="text-sm text-halo-text mb-3 leading-relaxed max-h-64 overflow-y-auto pr-2">
          {children}
        </div>
      )}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-halo-brown hover:underline mb-3"
      >
        {expanded ? 'Ocultar' : 'Leer texto completo'}
      </button>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testid}
        />
        <span className="text-halo-charcoal">He leído y acepto.</span>
      </label>
    </div>
  );
};

const StepPayment = ({ state, setState, submitting, onPay }) => {
  const originalTotal = useEstimatedTotal(state);
  const [validating, setValidating] = useState(false);

  const applied = state.promo_applied;
  let discount = 0;
  if (applied) {
    if (applied.discount_type === 'fixed') {
      discount = Math.min(originalTotal, applied.discount_amount || 0);
    } else {
      discount = originalTotal * ((applied.discount_percent || 0) / 100);
    }
  }
  const total = Math.max(0, originalTotal - discount);
  const deposit = (total * 0.5).toFixed(2);

  const applyPromo = async () => {
    const code = (state.promo_code || '').trim().toUpperCase();
    if (!code) {
      toast.error('Introduce un código');
      return;
    }
    setValidating(true);
    try {
      const res = await axios.get(
        `${API}/promo-codes/validate/${encodeURIComponent(code)}`
      );
      setState((p) => ({ ...p, promo_applied: res.data, promo_code: code }));
      toast.success(`Código ${code} aplicado`);
    } catch (err) {
      setState((p) => ({ ...p, promo_applied: null }));
      toast.error(err.response?.data?.detail || 'Código no válido');
    } finally {
      setValidating(false);
    }
  };

  const clearPromo = () => {
    setState((p) => ({ ...p, promo_applied: null, promo_code: '' }));
  };

  return (
    <Card title="Resumen y pago">
      <div className="space-y-2 bg-halo-cream rounded-xl p-4 mb-6 text-sm">
        <Row k="Tipo" v={typeLabel(state.booking_type)} />
        <Row
          k="Personas"
          v={`${state.num_adults} adultos${state.num_children ? `, ${state.num_children} niños` : ''}${
            state.num_babies ? `, ${state.num_babies} bebés` : ''
          }`}
        />
        <Row k="Fecha y hora" v={`${state.date} a las ${state.time}`} />
        <Row k="Cliente" v={state.customer.full_name} />
        <div className="border-t border-halo-taupe/15 my-2 pt-2">
          <div className="flex justify-between">
            <span>Subtotal:</span>
            <span>{originalTotal.toFixed(2)}€</span>
          </div>
          {applied && (
            <div className="flex justify-between text-halo-sage">
              <span>
                Descuento{' '}
                <span className="font-mono">[{applied.code}]</span>
                {applied.discount_type === 'fixed'
                  ? ''
                  : ` (-${applied.discount_percent}%)`}
                :
              </span>
              <span>-{discount.toFixed(2)}€</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Total:</span>
            <span>{total.toFixed(2)}€</span>
          </div>
          <div className="flex justify-between font-medium text-halo-brown">
            <span>A pagar ahora (50% depósito):</span>
            <span>{deposit}€</span>
          </div>
        </div>
      </div>

      <div className="mb-6" data-testid="promo-code-section">
        <label className="block text-xs font-medium text-halo-charcoal mb-1">
          ¿Tienes un código promocional?
        </label>
        {applied ? (
          <div className="flex items-center gap-2 bg-halo-sage/10 border border-halo-sage/30 rounded-lg px-3 py-2 text-sm">
            <Tag size={14} className="text-halo-sage" />
            <span className="font-mono font-bold text-halo-sage">
              {applied.code}
            </span>
            <span className="text-halo-text">
              aplicado ·{' '}
              {applied.discount_type === 'fixed'
                ? `-${applied.discount_amount}€`
                : `-${applied.discount_percent}%`}
            </span>
            <button
              type="button"
              onClick={clearPromo}
              className="ml-auto text-xs text-halo-brown hover:underline"
              data-testid="promo-clear"
            >
              quitar
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              placeholder="Ej: VANDALAS"
              value={state.promo_code}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  promo_code: e.target.value.toUpperCase(),
                }))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyPromo();
                }
              }}
              className="font-mono uppercase tracking-wider"
              data-testid="promo-code-input"
              disabled={validating}
            />
            <Button
              type="button"
              variant="outline"
              onClick={applyPromo}
              disabled={validating || !(state.promo_code || '').trim()}
              data-testid="promo-apply"
            >
              {validating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                'Aplicar'
              )}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-900 mb-6">
        📞 Tras el pago, tu reserva quedará <strong>pendiente de confirmar</strong>. Te
        llamaremos para confirmarla. La diferencia se abona al venir al centro.
      </div>

      <Button
        onClick={onPay}
        disabled={submitting}
        className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream py-4"
        data-testid="pay-now"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <Loader2 size={18} className="animate-spin" /> Redirigiendo a Redsys...
          </span>
        ) : (
          `Pagar ${deposit}€ con tarjeta`
        )}
      </Button>
      <p className="text-xs text-center text-halo-taupe mt-3">
        Pago seguro procesado por Redsys (CaixaBank).
      </p>
    </Card>
  );
};

const Row = ({ k, v }) => (
  <div className="flex justify-between">
    <span className="text-halo-text">{k}:</span>
    <span className="text-halo-charcoal font-medium text-right">{v}</span>
  </div>
);

function typeLabel(t) {
  if (t === 'solo_adultos') return 'Solo adultos';
  if (t === 'con_ninos') return 'Con niños';
  return 'Adulto con bebé';
}

// Placeholder price calculation; backend recalculates and is the source of truth.
function useEstimatedTotal(state) {
  const PRICE_PER_PERSON = 38; // fallback flat estimate
  return PRICE_PER_PERSON * (state.num_adults + state.num_children);
}

export default ReservarPage;
